import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./rules.css";
import "../color_pallet/colorPallet.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const GAME_TITLE_MAX_LEN = 100;
const GAME_SUB_TITLE_MAX_LEN = 300;

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [gameTitle, setGameTitle] = useState("");
  const [gameSubTitle, setGameSubTitle] = useState("");
  const [displayQuestionCount, setDisplayQuestionCount] = useState(10);
  const [shuffleQuestions, setShuffleQuestions] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(true);
  const [saving, setSaving] = useState(false);

  const availableQuestions = Array.isArray(data?.questions) ? data.questions.length : 0;
  const questionCap = availableQuestions > 0 ? availableQuestions : 10;

  const [colors, setColors] = useState({
    text_color: "#ffffff",
    button_color: "#ffffff",
    button_backgroundcolor: "#3B82F6",
    popup_background_color: "#1B1433",
    popup_text_color: "#ffffff",
  });

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;

    if ("game_page_title" in data || "game_title" in data || "landing_page_title" in data) {
      const t = String(data.game_page_title ?? data.game_title ?? data.landing_page_title ?? "");
      setGameTitle(t.slice(0, GAME_TITLE_MAX_LEN));
    }
    if ("game_page_sub_title" in data || "game_sub_title" in data) {
      setGameSubTitle(
        String(data.game_page_sub_title ?? data.game_sub_title ?? "").slice(0, GAME_SUB_TITLE_MAX_LEN)
      );
    }

    const rawCount = parseInt(String(data.display_question_count ?? "10"), 10);
    const cap = Array.isArray(data.questions) && data.questions.length > 0 ? data.questions.length : 10;
    setDisplayQuestionCount(
      Math.min(cap, Math.max(1, Number.isFinite(rawCount) && rawCount > 0 ? rawCount : Math.min(10, cap)))
    );
    setShuffleQuestions(String(data.shuffle_questions ?? "false").trim().toLowerCase() === "true");
    setShowLeaderboard(String(data.show_leaderboard ?? "true").trim().toLowerCase() !== "false");

    setColors((prev) => ({
      text_color: data.text_color ?? prev.text_color,
      button_color: data.button_color ?? data.button_text_color ?? prev.button_color,
      button_backgroundcolor:
        data.button_backgroundcolor ?? data.button_background_color ?? prev.button_backgroundcolor,
      popup_background_color: data.popup_background_color ?? prev.popup_background_color,
      popup_text_color: data.popup_text_color ?? prev.popup_text_color,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleGameTitleChange = (val) => {
    let next = val;
    if (next.length > GAME_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Game title can be at most ${GAME_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, GAME_TITLE_MAX_LEN);
    }
    setGameTitle(next);
  };

  const handleSubmit = async () => {
    if (!currentTheme || !adminToken) {
      Swal.fire("No theme", "Select a theme before saving.", "warning");
      return;
    }

    const payload = {
      data: {
        game_page_title: String(gameTitle ?? "").slice(0, GAME_TITLE_MAX_LEN),
        game_title: String(gameTitle ?? "").slice(0, GAME_TITLE_MAX_LEN),
        game_page_sub_title: String(gameSubTitle ?? "").slice(0, GAME_SUB_TITLE_MAX_LEN),
        game_sub_title: String(gameSubTitle ?? "").slice(0, GAME_SUB_TITLE_MAX_LEN),
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_text_color: colors.button_color,
        button_backgroundcolor: colors.button_backgroundcolor,
        button_background_color: colors.button_backgroundcolor,
        popup_background_color: colors.popup_background_color,
        popup_text_color: colors.popup_text_color,
        display_question_count: Math.min(
          questionCap,
          Math.max(1, Number(displayQuestionCount) || 1)
        ),
        shuffle_questions: shuffleQuestions ? "true" : "false",
        show_leaderboard: showLeaderboard ? "true" : "false",
        additional_fields: "false",
      },
      currentTheme,
    };

    setSaving(true);
    try {
      await updateThemeData({ payload, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Game settings saved successfully.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container" data-tour="rules-colours-link">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Colours &amp; situations
                </div>
                <div className="code">
                  Add up to 9 colours, then assign 1–3 colour values as answers per question.
                </div>
                <button
                  type="button"
                  className="add-btn"
                  style={{ marginTop: 16 }}
                  onClick={() => navigate("/admin/customize-colours")}
                >
                  Manage colours &amp; questions
                </button>
              </div>
            </div>

            <div className="rules-page-center">
              <div className="admin-tab-container" data-tour="rules-game-settings">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Settings
                </div>

                <div className="form-group-v1">
                  <label>Game title</label>
                  <textarea
                    value={gameTitle}
                    onChange={(e) => handleGameTitleChange(e.target.value)}
                    placeholder="Game title"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={GAME_TITLE_MAX_LEN}
                  />
                  <div className="code">* Maximum {GAME_TITLE_MAX_LEN} characters.</div>
                </div>

                <div className="form-group-v1">
                  <label>Questions to show</label>
                  <input
                    type="number"
                    min={1}
                    max={questionCap}
                    value={displayQuestionCount}
                    className="form-control"
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isFinite(n)) {
                        setDisplayQuestionCount(1);
                        return;
                      }
                      if (n > questionCap) {
                        Swal.fire(
                          "Limit",
                          `You can show at most ${questionCap} question${questionCap === 1 ? "" : "s"} (that is how many exist).`,
                          "warning"
                        );
                        setDisplayQuestionCount(questionCap);
                        return;
                      }
                      setDisplayQuestionCount(Math.max(1, n));
                    }}
                  />
                  <div className="code">
                    * Default 10. Cannot be more than the {questionCap} available question
                    {questionCap === 1 ? "" : "s"}.
                  </div>
                </div>

                <div className="rules-toggle-row">
                  <div>
                    <div className="rules-toggle-title">Shuffle questions</div>
                    <div className="code">When on, players see questions in a random order.</div>
                  </div>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={shuffleQuestions}
                      onChange={setShuffleQuestions}
                      onColor="#1a2b6d"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>{shuffleQuestions ? "On" : "Off"}</span>
                  </div>
                </div>

                <div className="rules-toggle-row">
                  <div>
                    <div className="rules-toggle-title">Show leaderboard</div>
                    <div className="code">When off, the Leaderboard button is hidden after the game.</div>
                  </div>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={showLeaderboard}
                      onChange={setShowLeaderboard}
                      onColor="#1a2b6d"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>{showLeaderboard ? "On" : "Off"}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container" data-tour="rules-colors">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Colors
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_backgroundcolor}
                          onChange={handleColorChange("button_backgroundcolor")}
                        />
                        <label>Button Background Color</label>
                      </div>
                      <div className="color-code">{colors.button_backgroundcolor}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.popup_background_color}
                          onChange={handleColorChange("popup_background_color")}
                        />
                        <label>Popup Background Color</label>
                      </div>
                      <div className="color-code">{colors.popup_background_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.popup_text_color}
                          onChange={handleColorChange("popup_text_color")}
                        />
                        <label>Popup Text Color</label>
                      </div>
                      <div className="color-code">{colors.popup_text_color}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder" data-tour="rules-save">
            <button className="save-and-continue" onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
